import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, EyeOff, Lock, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('reason') === 'idle') {
      toast.info(t('auth.idleLogoutMessage'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(t('auth.enterCredentials'));
      return;
    }
    setLoading(true);
    try {
      const data = await login(email, password, remember);
      toast.success(t('auth.welcome'));
      navigate(data?.user?.role === 'contractor' ? '/portal' : '/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ background: 'linear-gradient(145deg, #0A1628 0%, #0F172A 40%, #162032 100%)' }}>
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[15%] left-[10%] w-1.5 h-1.5 bg-[#3399FF]/20 rounded-full animate-float" />
        <div className="absolute top-[25%] right-[15%] w-2 h-2 bg-[#66CCFF]/15 rounded-full animate-float delay-200" />
        <div className="absolute bottom-[30%] left-[20%] w-1 h-1 bg-[#3399FF]/25 rounded-full animate-float delay-300" />
        <div className="absolute top-[60%] right-[25%] w-1.5 h-1.5 bg-[#66CCFF]/20 rounded-full animate-float delay-150" />
        <div className="absolute bottom-[15%] right-[10%] w-2 h-2 bg-[#3399FF]/15 rounded-full animate-float delay-400" />
        {/* Gradient orb */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#003366]/8 rounded-full blur-[120px]" />
      </div>

      <div className="w-full max-w-[420px] mx-4 sm:mx-auto px-1 relative z-10">
        {/* Back to website */}
        <button
          data-testid="back-to-website-btn"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-slate-400 hover:text-[#66CCFF] text-sm font-medium mb-6 transition-colors mx-auto"
        >
          <ArrowLeft className="w-4 h-4" /> Back to website
        </button>

        {/* Logo */}
        <div className="flex flex-col items-center mb-8 sm:mb-10 animate-fade-in-up">
          <div className="relative">
            <div className="absolute inset-0 bg-[#3399FF]/20 rounded-full blur-xl scale-150" />
            <img
              src="https://customer-assets.emergentagent.com/job_blue-well-crm/artifacts/vyxcu57o_image.png"
              alt="Blue Well Drilling"
              className="w-20 h-20 sm:w-24 sm:h-24 object-contain relative z-10 drop-shadow-2xl"
            />
          </div>
        </div>

        {/* Login Card */}
        <div className="animate-fade-in-up delay-100 bg-white/[0.06] backdrop-blur-2xl border border-white/[0.08] rounded-2xl p-6 sm:p-8 shadow-[0_8px_64px_rgba(0,0,0,0.4)]">
          <div className="text-center mb-8">
            <h1
              data-testid="login-title"
              className="text-2xl font-bold text-white font-['Barlow'] tracking-tight"
            >
              Employee Access
            </h1>
            <p className="text-[#66CCFF] text-sm font-medium mt-1.5 flex items-center justify-center gap-1.5">
              <Lock className="w-3.5 h-3.5" />
              Blue Well Drilling · Staff Portal
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-sm text-slate-300 font-medium">{t('auth.email')}</Label>
              <Input
                data-testid="login-email"
                type="email"
                inputMode="email"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                className="bg-white/[0.05] border-white/[0.1] text-white placeholder:text-slate-500 h-12 text-base rounded-xl focus:border-[#3399FF] focus:ring-[#3399FF]/20 transition-all"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm text-slate-300 font-medium">{t('auth.password')}</Label>
              <div className="relative">
                <Input
                  data-testid="login-password"
                  type={showPass ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.passwordPlaceholder')}
                  className="bg-white/[0.05] border-white/[0.1] text-white placeholder:text-slate-500 h-12 text-base rounded-xl pr-10 focus:border-[#3399FF] focus:ring-[#3399FF]/20 transition-all"
                />
                <button
                  type="button"
                  data-testid="toggle-password-btn"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <label
              htmlFor="remember-me"
              className="flex items-center gap-2.5 pt-1 cursor-pointer select-none"
            >
              <Checkbox
                id="remember-me"
                data-testid="remember-me-checkbox"
                checked={remember}
                onCheckedChange={(v) => setRemember(!!v)}
                className="border-white/30 data-[state=checked]:bg-[#3399FF] data-[state=checked]:border-[#3399FF]"
              />
              <span className="text-sm text-slate-300">{t('auth.rememberMe')}</span>
            </label>

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#003366] hover:bg-[#004080] text-white font-semibold rounded-xl shadow-lg shadow-[#003366]/30 transition-all duration-200 active:scale-[0.98] mt-2"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('auth.loggingIn')}
                </span>
              ) : (
                t('auth.loginButton')
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-slate-600 text-xs mt-8 animate-fade-in delay-300">
          {t('auth.copyright')}
        </p>
      </div>
    </div>
  );
}
